import { sidebarOverlayActiveAtom } from '@/atoms/sidebarPanelAtoms';
import {
  SIDEBAR_OVERLAY_OPEN_ATTR,
  SIDEBAR_OVERLAY_OPEN_VALUE,
  SIDEBAR_OVERLAY_TRIGGER_ATTR,
  SIDEBAR_OVERLAY_TRIGGER_VALUE,
} from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { getDefaultStore } from 'jotai';

/**
 * MutationObserver for Radix UI dropdown/popover portals.
 *
 * When a user clicks a dropdown inside the sidebar, the dropdown content renders
 * in a Radix portal OUTSIDE the sidebar DOM. This triggers mouseLeave on the sidebar.
 * Without this observer, the sidebar would hide while the dropdown is still open.
 *
 * The observer watches for:
 * 1. The dropdown trigger's data-state changing from "open" to "closed"
 * 2. The portal element being removed from the DOM
 *
 * When either occurs, we schedule a hide (which mouseEnter will cancel if user returns).
 *
 * Note: This is NOT about the sidebar panel's own positioning (which uses CSS
 * position:fixed, not a portal). This specifically handles Radix UI component portals.
 */
let radixPortalObserver: MutationObserver | null = null;

export function cleanupRadixPortalObserver() {
  if (radixPortalObserver) {
    radixPortalObserver.disconnect();
    radixPortalObserver = null;
  }
}

export function isSessionsOverlaySensitiveContent(content: string | null): boolean {
  return content === 'sessions';
}

const MARKED_OVERLAY_TRIGGER_SELECTOR = `[${SIDEBAR_OVERLAY_TRIGGER_ATTR}="${SIDEBAR_OVERLAY_TRIGGER_VALUE}"]`;

function getControlledContentElement(trigger: Element): Element | null {
  const controlledContentId = trigger.getAttribute('aria-controls');
  if (!controlledContentId) {
    return null;
  }

  return document.getElementById(controlledContentId);
}

function isControlledContentOpen(trigger: Element): boolean {
  const controlledContent = getControlledContentElement(trigger);
  if (!controlledContent) {
    return false;
  }

  const controlledState = controlledContent.getAttribute('data-state');
  if (controlledState) {
    return controlledState === 'open';
  }

  if (
    controlledContent.getAttribute('aria-hidden') === 'true' ||
    controlledContent.hasAttribute('hidden')
  ) {
    return false;
  }

  return true;
}

function isTriggerOpen(trigger: Element): boolean {
  return (
    trigger.getAttribute(SIDEBAR_OVERLAY_OPEN_ATTR) === SIDEBAR_OVERLAY_OPEN_VALUE ||
    trigger.getAttribute('data-state') === 'open' ||
    trigger.getAttribute('aria-expanded') === 'true' ||
    isControlledContentOpen(trigger)
  );
}

function findOpenOverlayTrigger(sidebar: HTMLElement): Element | null {
  const triggers = sidebar.querySelectorAll(MARKED_OVERLAY_TRIGGER_SELECTOR);
  return Array.from(triggers).find((trigger) => isTriggerOpen(trigger)) ?? null;
}

function getRadixPortalElement(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) {
    return null;
  }
  return target.closest('[data-radix-popper-content-wrapper]');
}

function getOwnedRadixPortalForTrigger(trigger: Element): Element | null {
  const controlledContentElement = getControlledContentElement(trigger);
  if (!controlledContentElement) {
    return null;
  }

  return (
    controlledContentElement.closest('[data-radix-popper-content-wrapper]') ??
    controlledContentElement
  );
}

/**
 * Watches a trigger (and optional portal) for close signals, then fires `onClose`.
 *
 * ⚠️ Coupling note: `requestOverlayClose` dispatches synthetic
 * `KeyboardEvent('keydown', { key: 'Escape' })` to force-close Radix overlays.
 * This relies on Radix's current behaviour of closing on un-trusted Escape keydown
 * events. If Radix changes to check `isTrusted`, `cancelable`, or switches to
 * `keyup`, the force-close path will silently break and need updating.
 */
function watchOverlayClose(options: {
  trigger: Element;
  portal?: Element;
  onClose: () => void;
}) {
  cleanupRadixPortalObserver();
  getDefaultStore().set(sidebarOverlayActiveAtom, true);

  const observer = new MutationObserver((_mutations, obs) => {
    const triggerRemoved = !document.body.contains(options.trigger);
    const portalRemoved = options.portal
      ? !document.body.contains(options.portal)
      : false;

    if (triggerRemoved || portalRemoved) {
      obs.disconnect();
      radixPortalObserver = null;
      getDefaultStore().set(sidebarOverlayActiveAtom, false);
      options.onClose();
      return;
    }

    if (!isTriggerOpen(options.trigger)) {
      // Wait for portal teardown before closing panel content to avoid
      // visible top-left anchor flicker during Radix close animations.
      if (options.portal && document.body.contains(options.portal)) {
        return;
      }
      obs.disconnect();
      radixPortalObserver = null;
      getDefaultStore().set(sidebarOverlayActiveAtom, false);
      options.onClose();
    }
  });

  observer.observe(options.trigger, {
    attributes: true,
    attributeFilter: ['data-state', 'aria-expanded', SIDEBAR_OVERLAY_OPEN_ATTR],
  });
  // Only watch direct children of body for portal attach/detach events.
  observer.observe(document.body, { childList: true });
  radixPortalObserver = observer;
}

function watchPortalRemoval(portal: Element, onClose: () => void) {
  cleanupRadixPortalObserver();
  getDefaultStore().set(sidebarOverlayActiveAtom, true);

  const observer = new MutationObserver((_mutations, obs) => {
    if (!document.body.contains(portal)) {
      obs.disconnect();
      radixPortalObserver = null;
      getDefaultStore().set(sidebarOverlayActiveAtom, false);
      onClose();
    }
  });

  observer.observe(document.body, { childList: true });
  radixPortalObserver = observer;
}

export function keepSidebarOpenForOverlayIfNeeded(options: {
  sidebar: HTMLElement | null;
  portalTarget: EventTarget | null;
  onClose: () => void;
}): boolean {
  const portal = getRadixPortalElement(options.portalTarget);
  const openTrigger = options.sidebar ? findOpenOverlayTrigger(options.sidebar) : null;

  if (openTrigger) {
    const ownedPortal = getOwnedRadixPortalForTrigger(openTrigger);
    watchOverlayClose({
      trigger: openTrigger,
      portal: portal ?? ownedPortal ?? undefined,
      onClose: options.onClose,
    });
    return true;
  }

  if (portal) {
    watchPortalRemoval(portal, options.onClose);
    return true;
  }

  return false;
}

export function shouldKeepOpenForPanelInteraction(
  sidebar: HTMLElement | null,
  options?: { respectFocus?: boolean }
): boolean {
  if (!sidebar) {
    return false;
  }

  const isHovering = sidebar.matches(':hover');

  if (isHovering) {
    return true;
  }

  if (!options?.respectFocus) {
    return false;
  }

  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }

  const isFocusInsideSidebar = sidebar.contains(activeElement);

  if (!isFocusInsideSidebar) {
    return false;
  }

  // When a dropdown/popover inside the panel closes, Radix auto-returns focus
  // to the trigger element. This shouldn't prevent the panel from reverting
  // on mouse leave — it's not intentional user interaction.
  if (
    activeElement.getAttribute(SIDEBAR_OVERLAY_TRIGGER_ATTR) ===
      SIDEBAR_OVERLAY_TRIGGER_VALUE &&
    !isTriggerOpen(activeElement)
  ) {
    return false;
  }

  return true;
}

export function hasAnyRadixPortalInDom(): boolean {
  return Boolean(document.body.querySelector('[data-radix-popper-content-wrapper]'));
}

export function getAnyRadixPortalInDom(): Element | null {
  return document.body.querySelector('[data-radix-popper-content-wrapper]');
}

export function hasOpenOverlayTriggerInSidebar(sidebar: HTMLElement | null): boolean {
  if (!sidebar) {
    return false;
  }

  return Boolean(findOpenOverlayTrigger(sidebar));
}

export function requestOverlayClose(sidebar: HTMLElement | null) {
  if (!sidebar) {
    return;
  }

  // Sidebar-scoped close request first (trigger/content/owned portal).
  // Only dispatch Escape for triggers that are currently open to avoid
  // emitting unnecessary key events across large session lists.
  const markedTriggers = sidebar.querySelectorAll(MARKED_OVERLAY_TRIGGER_SELECTOR);
  markedTriggers.forEach((trigger) => {
    if (!(trigger instanceof HTMLElement) || !isTriggerOpen(trigger)) {
      return;
    }

    const controlledContentElement = getControlledContentElement(trigger);
    if (controlledContentElement instanceof HTMLElement) {
      controlledContentElement.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          bubbles: true,
        })
      );
    }

    const ownedPortal = getOwnedRadixPortalForTrigger(trigger);
    if (ownedPortal instanceof HTMLElement) {
      ownedPortal.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          bubbles: true,
        })
      );
    }

    // Radix components generally close on Escape when open.
    trigger.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        bubbles: true,
      })
    );
  });
}
